//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import SoilTypeModel from './soilTypeModel';

/**
 * @service SoilTypeService
 * @description Service for managing Soil Type definitions within the aquaculture system.
 * Soil Types categorize the physical composition of the pond bottom (e.g., Clay, Sandy Loam, Peat).
 *
 * Understanding soil type is crucial for:
 * 1. **Pond Preparation**: Determining lime requirements and fertilization strategies.
 * 2. **Water Quality Management**: Different soils interact differently with water (e.g., acid sulfate soils).
 * 3. **Species Selection**: Some species prefer specific bottom substrates.
 *
 * The service interacts with the `/aqu/soiltype/` backend endpoint.
 *
 * @feature 1. Retrieve all defined Soil Types.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all types are loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<SoilTypeModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/soiltype/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class SoilTypeService extends Service<SoilTypeModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/soiltype/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/soiltype/');
  }

  /**
   * @method getAllSoilTypes
   * @async
   * @description Fetch all Soil Type records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all types are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<SoilTypeModel>>} Promise resolving to an array of SoilTypeModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * soilTypeService.getAllSoilTypes().then(types => {
   *   console.log('Soil Types:', types);
   * });
   */
  public getAllSoilTypes() {
    return new Promise<Array<SoilTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<SoilTypeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
